/**
 * AssetDiscovery.js
 * Full asset inventory — hardware specs + software inventory per device.
 * Data is collected by the agent on every validate cycle and persisted to DB.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const CAT_COLOR = {
  browser:       { bg: '#dbeafe', color: '#1d4ed8' },
  security:      { bg: '#fee2e2', color: '#dc2626' },
  office:        { bg: '#dcfce7', color: '#16a34a' },
  development:   { bg: '#fef3c7', color: '#d97706' },
  creative:      { bg: '#ede9fe', color: '#7c3aed' },
  communication: { bg: '#f0fdf4', color: '#15803d' },
  system:        { bg: '#f1f5f9', color: '#475569' },
  driver:        { bg: '#fff7ed', color: '#c2410c' },
  other:         { bg: '#f8fafc', color: '#94a3b8' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function timeSince(d) {
  if (!d) return 'never';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 60)  return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m/60)}h ago`;
  return `${Math.floor(m/1440)}d ago`;
}

function HardwareBadge({ device }) {
  const vendor = device.isDell ? '🔵 Dell' : device.isHP ? '🔵 HP' : device.isSurface ? '🔵 Surface' : device.isLenovo ? '🔵 Lenovo' : '⚪ Generic';
  return <span style={{ fontSize: 11, color: '#64748b' }}>{vendor}</span>;
}

function DiskBar({ used, total }) {
  if (!total) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const pct  = Math.round((used / total) * 100);
  const color = pct > 90 ? '#dc2626' : pct > 70 ? '#d97706' : '#16a34a';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, background: '#f0f4ff', borderRadius: 4, height: 6, overflow: 'hidden', minWidth: 60 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

export default function AssetDiscovery() {
  const [devices,  setDevices]  = useState([]);
  const [software, setSoftware] = useState([]);
  const [selDevice, setSelDevice] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [swLoading, setSwLoading] = useState(false);
  const [tab,      setTab]      = useState('assets');
  const [search,   setSearch]   = useState('');
  const [cardFilter, setCardFilter] = useState('all'); // 'all' | 'hasInventory' | 'hasSoftware'
  const [catFilter, setCatFilter] = useState('all');
  const [swSearch, setSwSearch] = useState('');
  const [msg,      setMsg]      = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg('');  // always clear previous error on reload
    try {
      const res = await api.getAssetInventory();
      setDevices(res.data.devices || []);
    } catch (e) {
      setMsg('Failed to load asset inventory: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function loadSoftware(device) {
    setSelDevice(device);
    setSwLoading(true);
    setSwSearch('');
    setCatFilter('all');
    setSoftware([]);   // clear previous device's software before loading new
    setMsg('');        // clear any previous error
    setTab('software');
    try {
      const res = await api.getDeviceSoftware(device.id);
      setSoftware(res.data || []);
    } catch (e) {
      setMsg('Failed to load software inventory: ' + (e.response?.data?.error || e.message));
    } finally {
      setSwLoading(false);
    }
  }

  // Summary stats
  const totalRam  = devices.reduce((s, d) => s + (d.ramGb || 0), 0);
  const totalDisk = devices.reduce((s, d) => s + (d.diskGb || 0), 0);
  const withInventory = devices.filter(d => d.lastInventoryAt).length;

  // Filter devices
  const filteredDevices = devices.filter(d => {
    const matchCard = cardFilter === 'all' ? true
      : cardFilter === 'hasInventory' ? !!d.lastInventoryAt
      : cardFilter === 'hasSoftware'  ? (d.softwareCount > 0)
      : true;
    const matchSearch = !search || [d.hostname, d.hwManufacturer, d.hwModel, d.os, d.ipAddress, d.serialNumber]
      .join(' ').toLowerCase().includes(search.toLowerCase());
    return matchCard && matchSearch;
  });

  // Filter software
  const filteredSoftware = software.filter(s => {
    const matchCat = catFilter === 'all' || s.category === catFilter;
    const matchSearch = !swSearch || [s.product, s.publisher, s.version]
      .join(' ').toLowerCase().includes(swSearch.toLowerCase());
    return matchCat && matchSearch;
  });

  const categories = [...new Set(software.map(s => s.category))].sort();

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🖥 Asset Discovery</div>
          <div className="page-subtitle">
            Hardware inventory, software catalog, and system specs per endpoint
          </div>
        </div>
        <button className="refresh-btn" onClick={load} disabled={loading}>↻ Refresh</button>
      </div>

      {msg && <div className="alert error" style={{ marginBottom: 16 }}>{msg}</div>}

      {/* KPI cards */}
      <div className="cards-row" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 20 }}>
        <div className="card blue" style={{ cursor:'pointer' }}
          onClick={() => { setCardFilter('all'); setTab('assets'); }}>
          <div className="card-label">Total Endpoints</div>
          <div className="card-value">{devices.length}</div>
          <div className="card-sub">{cardFilter==='all' ? '▼ showing all' : 'click to show all'}</div>
        </div>
        <div className="card green"
          style={{ cursor:'pointer', outline: cardFilter==='hasInventory'?'2px solid #16a34a':undefined }}
          onClick={() => { setCardFilter(cardFilter==='hasInventory'?'all':'hasInventory'); setTab('assets'); }}>
          <div className="card-label">Inventory Collected</div>
          <div className="card-value">{withInventory}</div>
          <div className="card-sub">{cardFilter==='hasInventory' ? '▼ filtered' : `of ${devices.length} devices`}</div>
        </div>
        <div className="card cyan">
          <div className="card-label">Total RAM</div>
          <div className="card-value">{Math.round(totalRam)} GB</div>
          <div className="card-sub">across all devices</div>
        </div>
        <div className="card amber">
          <div className="card-label">Total Storage</div>
          <div className="card-value">{Math.round(totalDisk)} GB</div>
          <div className="card-sub">C: drive total</div>
        </div>
        <div className="card purple"
          style={{ cursor:'pointer', outline: cardFilter==='hasSoftware'?'2px solid #7c3aed':undefined }}
          onClick={() => { setCardFilter(cardFilter==='hasSoftware'?'all':'hasSoftware'); setTab('assets'); }}>
          <div className="card-label">Avg Software</div>
          <div className="card-value">
            {withInventory > 0 ? Math.round(devices.filter(d => d.softwareCount).reduce((s, d) => s + d.softwareCount, 0) / withInventory) : '—'}
          </div>
          <div className="card-sub">{cardFilter==='hasSoftware' ? '▼ filtered' : 'apps per device'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e8ecf4', marginBottom: 20 }}>
        {[
          { id: 'assets',   label: `🖥 Hardware Assets (${devices.length})` },
          { id: 'software', label: selDevice ? `📦 Software — ${selDevice.hostname} (${software.length})` : '📦 Software Inventory' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? '#1a56db' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #1a56db' : '2px solid transparent',
            marginBottom: -2
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ASSETS TAB ── */}
      {tab === 'assets' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <input className="form-input" style={{ maxWidth: 320 }}
              placeholder="Search hostname, model, serial, IP..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">
                <div className="panel-title-icon">🖥</div>
                Hardware Asset Inventory
              </div>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                Updated on each agent validate cycle (~10 min)
              </span>
            </div>
            {loading ? <div className="loading">Loading assets...</div> : (
              <table>
                <thead>
                  <tr>
                    <th>Hostname</th>
                    <th>Vendor / Model</th>
                    <th>Serial No.</th>
                    <th>OS</th>
                    <th>CPU</th>
                    <th>RAM</th>
                    <th>Disk (C:)</th>
                    <th>BIOS</th>
                    <th>Logged In User</th>
                    <th>IP</th>
                    <th>Last Inventory</th>
                    <th>Software</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.length === 0 && (
                    <tr><td colSpan={11} className="empty">
                      {devices.length === 0
                        ? 'No devices registered. Deploy the agent to endpoints.'
                        : 'No devices match your search.'}
                    </td></tr>
                  )}
                  {filteredDevices.map(d => (
                    <tr key={d.id}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{d.hostname}</div>
                        <span className={`badge ${d.status === 'online' ? 'online' : 'offline'}`} style={{ fontSize: 10 }}>
                          {d.status === 'online' ? '● Online' : '○ Offline'}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>{d.hwManufacturer || '—'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.hwModel || '—'}</div>
                        <HardwareBadge device={d} />
                      </td>
                      <td>
                        <code style={{ fontSize: 11, color: '#64748b', background: '#f8fafc',
                          padding: '1px 6px', borderRadius: 4 }}>
                          {d.serialNumber || '—'}
                        </code>
                      </td>
                      <td>
                        <div style={{ fontSize: 12 }}>{d.os}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.osVersion}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: 12, maxWidth: 160 }}>{d.cpuModel || '—'}</div>
                        {d.cpuCores && <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.cpuCores} cores</div>}
                      </td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {d.ramGb ? `${d.ramGb} GB` : '—'}
                        </div>
                      </td>
                      <td style={{ minWidth: 120 }}>
                        {d.diskGb ? (
                          <>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>
                              {d.diskFreeGb ? `${d.diskFreeGb} GB free / ${d.diskGb} GB` : `${d.diskGb} GB`}
                            </div>
                            <DiskBar used={d.diskGb - (d.diskFreeGb || 0)} total={d.diskGb} />
                          </>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: 11, color: '#94a3b8' }}>{d.biosVersion || '—'}</td>
                      <td>
                        {(() => {
                          // Strip 'last:' prefix that may come from old server versions
                          const rawUser = d.loggedInUser || '';
                          const isLast = rawUser.startsWith('last:');
                          const username = isLast ? rawUser.slice(5) : rawUser;
                          const isActive = d.loggedInUserActive && !isLast;
                          const shortName = username.includes('\\') ? username.split('\\').pop() : username;
                          const domain = username.includes('\\') ? username.split('\\')[0] : null;

                          return username ? (
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? '#0e7c3a' : '#1e2a3b' }}>
                                {isActive ? '🟢' : '👤'} {shortName}
                              </div>
                              {domain && (
                                <div style={{ fontSize: 10, color: '#94a3b8' }}>{domain}</div>
                              )}
                              <div style={{ fontSize: 10, color: isActive ? '#16a34a' : '#94a3b8' }}>
                                {isActive ? 'Active session' : 'Last logged in'}
                              </div>
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>No user</span>
                          );
                        })()}
                      </td>
                      <td style={{ fontSize: 12 }}>{d.ipAddress || '—'}</td>
                      <td style={{ fontSize: 11, color: '#94a3b8' }}>
                        {d.lastInventoryAt ? timeSince(d.lastInventoryAt) : (
                          <span style={{ color: '#f59e0b' }}>Pending</span>
                        )}
                      </td>
                      <td>
                        {d.softwareCount ? (
                          <button className="btn btn-sm btn-outline" onClick={() => loadSoftware(d)}>
                            📦 {d.softwareCount} apps
                          </button>
                        ) : (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>No data yet</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── SOFTWARE TAB ── */}
      {tab === 'software' && (
        <>
          {!selDevice ? (
            <div className="panel" style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1e2a3b' }}>
                Select a device to view its software inventory
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>
                Click "📦 N apps" on any device in the Hardware Assets tab
              </div>
            </div>
          ) : (
            <>
              {/* Device summary bar */}
              <div className="panel" style={{ marginBottom: 16 }}>
                <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div className="flex-gap">
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#eff4ff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🖥</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{selDevice.hostname}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {selDevice.hwManufacturer} {selDevice.hwModel} · {selDevice.os} · {selDevice.ipAddress}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 20 }}>
                    {selDevice.cpuModel && <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>CPU</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{selDevice.cpuModel?.split(' ').slice(-2).join(' ')}</div>
                    </div>}
                    {selDevice.ramGb && <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>RAM</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{selDevice.ramGb} GB</div>
                    </div>}
                    {selDevice.diskGb && <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>Disk</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{selDevice.diskGb} GB</div>
                    </div>}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>Apps</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{software.length}</div>
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSelDevice(null); setTab('assets'); }}>
                    ← Back to Assets
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="form-input" style={{ maxWidth: 260 }}
                  placeholder="Search software..."
                  value={swSearch} onChange={e => setSwSearch(e.target.value)} />
                <select className="form-input" style={{ maxWidth: 160 }}
                  value={catFilter} onChange={e => setCatFilter(e.target.value)}>
                  <option value="all">All Categories</option>
                  {categories.map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
                <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
                  {filteredSoftware.length} of {software.length} apps
                </span>
              </div>

              {/* Category summary chips */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                {Object.entries(
                  software.reduce((acc, s) => { acc[s.category] = (acc[s.category] || 0) + 1; return acc; }, {})
                ).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                  <button key={cat}
                    onClick={() => setCatFilter(catFilter === cat ? 'all' : cat)}
                    style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      cursor: 'pointer', border: 'none',
                      background: catFilter === cat ? (CAT_COLOR[cat]?.color || '#64748b') : (CAT_COLOR[cat]?.bg || '#f1f5f9'),
                      color: catFilter === cat ? '#fff' : (CAT_COLOR[cat]?.color || '#64748b')
                    }}>
                    {cat} ({count})
                  </button>
                ))}
              </div>

              <div className="panel">
                <div className="panel-header">
                  <div className="panel-title">
                    <div className="panel-title-icon">📦</div>
                    Installed Software — {selDevice.hostname}
                  </div>
                </div>
                {swLoading ? <div className="loading">Loading software inventory...</div> : (
                  <table>
                    <thead>
                      <tr>
                        <th>Application</th>
                        <th>Publisher</th>
                        <th>Version</th>
                        <th>Category</th>
                        <th>Detected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSoftware.length === 0 && (
                        <tr><td colSpan={5} className="empty">
                          {software.length === 0
                            ? 'No software inventory yet. Agent will collect on next validate cycle.'
                            : 'No software matches your filter.'}
                        </td></tr>
                      )}
                      {filteredSoftware.map((s, i) => (
                        <tr key={i}>
                          <td><strong style={{ fontSize: 13 }}>{s.product}</strong></td>
                          <td style={{ fontSize: 12, color: '#64748b' }}>{s.publisher || '—'}</td>
                          <td>
                            <code style={{ fontSize: 11, background: '#f8fafc',
                              padding: '1px 6px', borderRadius: 4, color: '#374151' }}>
                              {s.version || '—'}
                            </code>
                          </td>
                          <td>
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                              background: CAT_COLOR[s.category]?.bg || '#f1f5f9',
                              color: CAT_COLOR[s.category]?.color || '#64748b'
                            }}>
                              {s.category}
                            </span>
                          </td>
                          <td style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(s.detectedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
